import { useCheckPermissionOR } from 'hooks/useCheckPermissions';
import { useUserInfo } from 'hooks/userInfoHook';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

/**
 * Determines whether the current user may see the edit controls
 * in the Locations section of a user profile.
 *
 * Returns true when ANY of the following is true:
 *  1. Owner profile      – the viewer is looking at their own profile.
 *  2. Super admin        – the current user is a super admin.
 *  3. Edit member        – the current user has the EDIT_MEMBER permission.
 *  4. Edit user (scoped) – the current user has EDIT_USER permission AND
 *                          their member id matches the profile owner's member id,
 *                          meaning they can only edit users within their own member.
 *
 */
export const useEditLocationsForMyProfile = ({
  currentId,
  currentMemberId,
} = {}) => {
  const checkPermissionOr = useCheckPermissionOR();

  const hasEditMemberPermission = checkPermissionOr([
    [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.MEMBER],
  ]);

  const hasEditUserPermission = checkPermissionOr([
    [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.USER],
  ]);

  const { userInfo: loginUserInfo } = useUserInfo();

  const isOwnerProfile = parseInt(loginUserInfo?.id) === parseInt(currentId);

  const hasEditUserScopedPermission =
    hasEditUserPermission &&
    parseInt(currentMemberId) === parseInt(loginUserInfo?.member?.id);

  return {
    hasEditLocationsForMyProfile:
      isOwnerProfile || hasEditMemberPermission || hasEditUserScopedPermission,
    hasEditMemberPermission,
    hasEditUserPermission,
  };
};
